import { Blockquote, Button, Modal, Stack, Text } from "@mantine/core"
import * as Sentry from "@sentry/react"
import { QueryErrorResetBoundary } from "@tanstack/react-query"
import { useRouter } from "next/navigation"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { AppTitle } from "./AppTitle"

export function AppErrorBoundary({ children }: { children: React.ReactNode }) {
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => {
        return (
          <Sentry.ErrorBoundary
            onReset={reset}
            fallback={ErrorFallback}
            beforeCapture={(scope, error) => {
              scope.setTag("location", "AppErrorBoundary")
              scope.setContext("componentStack", {
                value: (error as any)?.componentStack,
              })
            }}
            onError={(error, componentStack) => {
              Sentry.captureException(error, {
                tags: { boundary: "AppErrorBoundary" },
                extra: { componentStack },
              })
            }}
          >
            {children}
          </Sentry.ErrorBoundary>
        )
      }}
    </QueryErrorResetBoundary>
  )
}

function ErrorFallback({ error, resetError }: { error: any; resetError: () => void }) {
  const { t } = useTranslation()
  const router = useRouter()

  useEffect(() => {
    if (error?.response?.status === 401) {
      console.log("ErrorFallback: Unauthorized, redirecting to /login")
      router.replace("/login")
    }
  }, [error])

  if (error?.response?.status === 401) {
    return <div></div>
  }

  return (
    <Modal opened={true} onClose={resetError} title={<AppTitle>{t("Error")}</AppTitle>}>
      <Stack gap="xs">
        <Text>{t("Something went wrong")}:</Text>
        <Blockquote radius="md">{error.message}</Blockquote>
        <Button onClick={resetError}>{t("Try again")}</Button>
      </Stack>
    </Modal>
  )
}
